import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

type TrainingLevel = 'beginner' | 'intermediate' | 'advanced' | 'pro';
type ContentType = 'self-practice' | 'joy-fun-learning';

interface TrainingContentItem {
  id: string;
  trainingLevel: TrainingLevel;
  contentType: ContentType;
  title: string;
  description: string;
  tips: string;
  referenceVideos?: string[];
  isActive: boolean;
  displayOrder: number;
}

interface AdminTrainingContentMasterProps {
  sport: string;
  contentType: ContentType;
}

const emptyForm = {
  trainingLevel: 'beginner' as TrainingLevel,
  title: '',
  description: '',
  tips: '',
  referenceVideosText: '',
  isActive: true,
  displayOrder: 0,
};

const getTitle = (contentType: ContentType) =>
  contentType === 'self-practice' ? 'Self-Practice Master' : 'Enjoyment and Entertainment Master';

const AdminTrainingContentMaster: React.FC<AdminTrainingContentMasterProps> = ({ sport, contentType }) => {
  const [items, setItems] = useState<TrainingContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TrainingContentItem | null>(null);
  const [form, setForm] = useState({ ...emptyForm });

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ sport, contentType });
      const response = await fetch(`/api/admin/training-content-items?${params.toString()}`);
      const result = await response.json();
      if (!result.success) {
        setError(result.message || 'Failed to load training content items');
        setItems([]);
        return;
      }
      setItems(result.data || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load training content items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadItems();
  }, [sport, contentType]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (item: TrainingContentItem) => {
    setEditing(item);
    setForm({
      trainingLevel: item.trainingLevel,
      title: item.title,
      description: item.description || '',
      tips: item.tips || '',
      referenceVideosText: Array.isArray(item.referenceVideos) ? item.referenceVideos.join(', ') : '',
      isActive: item.isActive,
      displayOrder: item.displayOrder || 0,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    try {
      const payload = {
        sport,
        contentType,
        trainingLevel: form.trainingLevel,
        title: form.title.trim(),
        description: form.description.trim(),
        tips: form.tips.trim(),
        referenceVideos: form.referenceVideosText
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0),
        isActive: form.isActive,
        displayOrder: Number(form.displayOrder) || 0,
      };
      const url = editing ? `/api/admin/training-content-items/${editing.id}` : '/api/admin/training-content-items';
      const method = editing ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!result.success) {
        setError(result.message || 'Failed to save training content item');
        return;
      }
      setSuccess(editing ? 'Item updated' : 'Item created');
      setDialogOpen(false);
      await loadItems();
    } catch (e: any) {
      setError(e.message || 'Failed to save training content item');
    }
  };

  const handleDelete = async (id: string) => {
    const ok = window.confirm('Delete this item?');
    if (!ok) return;
    try {
      const response = await fetch(`/api/admin/training-content-items/${id}`, { method: 'DELETE' });
      const result = await response.json();
      if (!result.success) {
        setError(result.message || 'Failed to delete item');
        return;
      }
      setSuccess('Item deleted');
      await loadItems();
    } catch (e: any) {
      setError(e.message || 'Failed to delete item');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {getTitle(contentType)} - {sport}
        </Typography>
        <Button variant="contained" onClick={openCreate}>
          Add Item
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <CardContent>
          {loading ? (
            <Typography variant="body2">Loading...</Typography>
          ) : items.length === 0 ? (
            <Alert severity="info">No items yet. Add one to begin.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Level</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Tips</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.trainingLevel}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.title}</Typography>
                      {item.description && (
                        <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{item.tips || '-'}</TableCell>
                    <TableCell>{item.isActive ? 'Active' : 'Inactive'}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => openEdit(item)}>Edit</Button>
                      <Button size="small" color="error" onClick={() => handleDelete(item.id)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Item' : 'Add Item'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Training Level"
              value={form.trainingLevel}
              onChange={(e) => setForm((prev) => ({ ...prev, trainingLevel: e.target.value as TrainingLevel }))}
              fullWidth
            >
              <MenuItem value="beginner">Beginner</MenuItem>
              <MenuItem value="intermediate">Intermediate</MenuItem>
              <MenuItem value="advanced">Advanced</MenuItem>
              <MenuItem value="pro">Pro</MenuItem>
            </TextField>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Tips / Style Notes"
              value={form.tips}
              onChange={(e) => setForm((prev) => ({ ...prev, tips: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Reference Videos (comma separated)"
              value={form.referenceVideosText}
              onChange={(e) => setForm((prev) => ({ ...prev, referenceVideosText: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Display Order"
              type="number"
              value={form.displayOrder}
              onChange={(e) => setForm((prev) => ({ ...prev, displayOrder: Number(e.target.value) || 0 }))}
              fullWidth
            />
            <FormControlLabel
              control={<Checkbox checked={form.isActive} onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))} />}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTrainingContentMaster;
